import * as styles from './components/styles.scss';
import { ControlIcons } from 'owa-control-icons';
import { getNoteColor } from './selectors/getNoteColor';
import { LazyModule, LazyModuleType, LazyAction, createLazyComponent } from 'owa-bundling';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { Loading, NetworkError } from 'owa-timestream-loadingpanel';
import { noteName, noteTooltip, assetErrorNote } from './noteStrings.locstring.json';
import type { NotePayload } from './schema';
import { registerAsset, AssetAriaLabel, DEFAULT_MIN_ASSET_WIDTH } from 'owa-timestream-asset-sdk';
import { resetStore } from './actions';
import getBackgroundColor from './utils/getBackgroundColor';
import loc from 'owa-localize';

const lazyModule = new LazyModule(
    () => import(/* webpackChunkName: "CalendarBoardNoteAsset" */ './lazyIndex')
);

const LazyNoteView = createLazyComponent<
    { payload: NotePayload },
    LazyModuleType<typeof lazyModule>,
    {}
>(lazyModule, m => m.NoteView, Loading, NetworkError);

const lazyWarmUpNotesSDK = new LazyAction(lazyModule, m => m.warmUpNotesSDK);
const lazyOnNotesColorSet = new LazyAction(lazyModule, m => m.onNotesColorSet);
const lazyCreateNoteFromClipboardData = new LazyAction(
    lazyModule,
    m => m.createNoteFromClipboardData
);

function warmUpNotesSDK() {
    lazyWarmUpNotesSDK.importAndExecute();
}

function onNotesColorSet(color: string, payload: NotePayload) {
    lazyOnNotesColorSet.importAndExecute(color, payload);
}

function createNoteFromClipboardData(
    getData: (format: string) => string,
    items: DataTransferItemList | undefined
) {
    return lazyCreateNoteFromClipboardData.importAndExecute(getData, items);
}

const NOTE_TYPE = 'note';

export default registerAsset<NotePayload>({
    type: NOTE_TYPE,
    iconName: ControlIcons.InsertTextBox,
    // disable in cloud cache scenarios because notes don't support them
    isAssetEnabled: () => !getUserConfiguration().SessionSettings?.IsShadowMailbox,
    getToolName: () => loc(noteName),
    getTooltip: () => loc(noteTooltip),
    getErrorMessage: () => loc(assetErrorNote),
    unsupportedDefaultCommands: [],
    CanvasView: LazyNoteView,
    viewStyle: styles.viewContainer,
    createEmptyAsset: () => ({
        type: NOTE_TYPE,
        width: DEFAULT_MIN_ASSET_WIDTH,
        height: 224,
        payload: {
            remoteId: '',
            title: undefined,
        },
    }),
    initializeAssetData: warmUpNotesSDK,
    cleanupAssetData: resetStore,
    createAssetFromClipboardData: createNoteFromClipboardData,
    onSetColor: onNotesColorSet,
    getAriaLabel: (payload: NotePayload) =>
        loc(
            AssetAriaLabel.assetAriaLabel,
            loc(noteName),
            payload.remoteId ? getNoteColor(payload.remoteId) : ''
        ),
    getBackgroundColor,
    getTitleString: (payload: NotePayload) => payload.title || loc(noteName),
    getSearchString: (payload: NotePayload) => payload.title || '', // we don't have access to note contents, so won't show in the search
    assetResizeConfiguration: () => ({ minHeight: 192 }),
});
